import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './About.css';

const values = [
  { icon: '🔭', title: 'Curiosity', desc: 'We never stop questioning, learning, and exploring new frontiers in electronics manufacturing.', color: 'card--indigo' },
  { icon: '🤝', title: 'Empathy', desc: "We put ourselves in our clients' shoes, understanding their pressures and delivering with care.", color: 'card--teal' },
  { icon: '🪟', title: 'Transparency', desc: 'Open communication at every stage — no surprises in pricing, timelines, or quality outcomes.', color: 'card--amber' },
  { icon: '🛡️', title: 'Integrity', desc: 'We do what we say. Every commitment is a promise we intend to keep, always.', color: 'card--rose' },
];

const capabilities = [
  { title: 'Conformal Coating', desc: 'Protective coating for PCBs operating in harsh environmental conditions — moisture, dust, and chemical resistance.', icon: '🛡️', bg: 'cap--blue' },
  { title: 'Testing & Validation', desc: 'ICT, functional testing, burn-in testing, and AOI with full traceability.', icon: '🔬', bg: 'cap--emerald' },
  { title: 'Statistical Process Control', desc: 'Real-time SPC monitoring across all SMT lines ensuring consistent quality and early defect detection.', icon: '📊', bg: 'cap--violet' },
  { title: 'Inventory & Logistics', desc: 'JIT delivery, bonded warehouse inventory management, and global logistics partnerships.', icon: '📦', bg: 'cap--orange' },
  { title: 'Component Sourcing', desc: 'Vetted global supply chain with counterfeit prevention protocols and approved vendor management.', icon: '🔗', bg: 'cap--cyan' },
  { title: 'Clean Room Manufacturing', desc: 'Class 100K clean room for precision medical and optical electronics assembly.', icon: '🏭', bg: 'cap--pink' },
];

const milestones = [
  { year: '2018', event: 'Founded in Bangalore with a vision to simplify electronics manufacturing for startups.', icon: '🚀' },
  { year: '2019', event: 'Launched first SMT line; completed 50+ NPI projects for domestic clients.', icon: '⚡' },
  { year: '2021', event: 'Expanded to 40,000 sq ft facility; added second SMT line and Class 100K clean room.', icon: '🏗️' },
  { year: '2022', event: 'Crossed 1M units/month production milestone; entered EV and medical device verticals.', icon: '🎯' },
  { year: '2024', event: 'Launched end-to-end EMS platform; now serving clients across 8+ countries.', icon: '🌍' },
];

const designServices = [
  { icon: '⚙️', title: 'End-to-End Engineering', desc: 'From initial concept to market-ready solutions — embedded systems, IoT hardware, and firmware.', color: 'ds--blue' },
  { icon: '📐', title: 'Mechanical Design', desc: 'Expert crafting of plastic and sheet metal enclosures with IP-rated environmental protection.', color: 'ds--green' },
  { icon: '🧪', title: 'Prototyping & Validation', desc: 'Rapid prototyping, rigorous validation testing, and complete regulatory certification support.', color: 'ds--purple' },
  { icon: '📡', title: 'PCB Layout & Routing', desc: 'High-density multi-layer PCB design with signal integrity, power integrity, and DFM validation.', color: 'ds--orange' },
];

const emsItems = [
  { n: '01', title: 'Integrated Component Sourcing', desc: 'Complete supply chain management eliminating fragmentation and ensuring component quality.', color: 'ems--a' },
  { n: '02', title: 'Agile Manufacturing Platform', desc: 'Flexible production from 100 prototype units to 1M+ mass production runs.', color: 'ems--b' },
  { n: '03', title: 'Quality Control & Testing', desc: 'In-house validation and testing ensuring design fidelity, reliability, and performance.', color: 'ems--c' },
  { n: '04', title: 'Logistics & Support', desc: 'Comprehensive warehousing, distribution, and after-sales support for seamless delivery.', color: 'ems--d' },
];

function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('revealed'); observer.unobserve(e.target); } }),
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

function CountUp({ target, suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        let start = 0;
        const num = parseInt(target);
        const step = Math.ceil(num / 40);
        const timer = setInterval(() => {
          start += step;
          if (start >= num) { setCount(num); clearInterval(timer); }
          else setCount(start);
        }, 30);
        observer.disconnect();
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);
  return <span ref={ref}>{count}{suffix}</span>;
}

export default function About() {
  useScrollReveal();

  return (
    <main className="about page-wrapper">

      {/* ── HERO ── */}
      <section className="about-hero">
        <div className="hero-particles">
          {[...Array(20)].map((_, i) => <div key={i} className="particle" style={{ '--i': i }} />)}
        </div>
        <div className="hero-grid-lines" />
        <div className="container about-hero__inner">
          <div className="hero-tag reveal">About Zxeon FS</div>
          <h1 className="about-hero__title reveal">
            Where Design Meets<br />
            <span className="gradient-text">Manufacturing Excellence</span>
          </h1>
          <p className="about-hero__sub reveal">
            Zxeon FS Private Limited is an electronics manufacturing platform delivering scalable EMS solutions — from design through delivery.
          </p>
          <div className="hero-scroll-indicator reveal">
            <span>Scroll to explore</span>
            <div className="scroll-dot" />
          </div>
        </div>
      </section>

      {/* ── OVERVIEW ── */}
      <section className="section overview-section">
        <div className="container">
          <div className="overview-grid">
            <div className="overview-text reveal slide-left">
              <div className="section-tag tag--blue">Who We Are</div>
              <h2 className="section-title">Bridging <span>Design &amp; Manufacturing</span></h2>
              <p className="body-text">
                <strong>Zxeon FS Private Limited</strong> represents the convergence of cutting-edge electronic product design with comprehensive turnkey manufacturing solutions — spanning the entire product lifecycle.
              </p>
              <p className="body-text">
                Our commitment centers on three pillars: <strong>defect-free quality</strong>, <strong>scalable production</strong>, and <strong>relentless innovation</strong>.
              </p>
              <div className="badges-row">
                {['ISO 9001 Certified', 'RoHS Compliant', 'IATF 16949 Ready', 'Class 100K Clean Room'].map(b => (
                  <div key={b} className="badge-pill"><span>✓</span>{b}</div>
                ))}
              </div>
            </div>

            <div className="overview-right reveal slide-right">
              <div className="facility-img-wrap">
                <img src="https://images.pexels.com/photos/1476321/pexels-photo-1476321.jpeg" alt="Zxeon FS Facility" />
                <div className="facility-overlay" />
                <span className="facility-label">🏭 Our Facility</span>
              </div>
              <div className="stats-quad">
                {[
                  { n: '6', suffix: '+', l: 'Years of Excellence' },
                  { n: '200', suffix: '+', l: 'Products Delivered' },
                  { n: '8', suffix: '+', l: 'Countries Served' },
                  { n: '40', suffix: 'K', l: 'Sq Ft Facility' },
                ].map((s, i) => (
                  <div key={s.l} className={`stat-card stat-card--${i}`}>
                    <strong><CountUp target={s.n} suffix={s.suffix} /></strong>
                    <span>{s.l}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── DESIGN SERVICES (alternating cards) ── */}
      <section className="section design-section">
        <div className="container">
          <div className="section-center reveal">
            <div className="section-tag tag--purple">Design Services</div>
            <h2 className="section-title">Electronic <span>Product Design Capabilities</span></h2>
          </div>
          <div className="design-grid">
            {designServices.map((d, i) => (
              <div key={d.title} className={`design-card ${d.color} reveal`} style={{ '--delay': `${i * 0.1}s` }}>
                <div className="design-card__icon">{d.icon}</div>
                <h3>{d.title}</h3>
                <p>{d.desc}</p>
                <div className="design-card__glow" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── EMS SECTION (alternating left/right) ── */}
      <section className="section ems-section">
        <div className="container">
          <div className="section-center reveal">
            <div className="section-tag tag--teal">EMS Solutions</div>
            <h2 className="section-title">Electronic <span>Manufacturing Services</span></h2>
          </div>
          <div className="ems-blocks">
            {emsItems.map((item, i) => (
              <div key={item.title} className={`ems-block ${i % 2 === 0 ? 'ems-block--normal' : 'ems-block--reverse'} reveal`}>
                <div className={`ems-visual ${item.color}`}>
                  <span className="ems-number">{item.n}</span>
                  <div className="ems-visual__ring" />
                </div>
                <div className="ems-content">
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BOX BUILD ── */}
      <section className="section boxbuild-section">
        <div className="container">
          <div className="section-center reveal">
            <div className="section-tag tag--orange">Integration</div>
            <h2 className="section-title">Box Build Assembly: <span>Complete Product Integration</span></h2>
          </div>
          <div className="boxbuild-card reveal">
            <div className="boxbuild-card__left">
              <div className="boxbuild-icon">📦</div>
              <h3>Full-Service Assembly</h3>
              <p>Zxeon FS delivers complete box build solutions that transform PCB assemblies into finished, customer-ready products. Our integrated approach combines precision assembly with comprehensive quality control.</p>
            </div>
            <div className="boxbuild-card__right">
              {['PCB integration into custom enclosures', 'Wire harnessing and mechanical assembly', 'Final product testing and validation', 'Manual inspection and specific packing'].map((item, i) => (
                <div key={i} className="boxbuild-item" style={{ '--delay': `${i * 0.1}s` }}>
                  <span className="boxbuild-check">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="section-center" style={{ marginTop: '48px' }}>
            <Link to="/contact" className="btn-glow">Get a Quote for Your Project →</Link>
          </div>
        </div>
      </section>

      {/* ── CULTURE ── */}
      <section className="section culture-section">
        <div className="culture-bg-pattern" />
        <div className="container">
          <div className="culture-grid">
            <div className="culture-text reveal slide-left">
              <div className="section-tag tag--indigo">Our Culture</div>
              <h2 className="section-title">Where Passion <span>Meets Purpose</span></h2>
              <p className="body-text">
                We believe great electronics don't just come from great machines — they come from great people. Our team is built on intellectual curiosity, collaborative problem-solving, and genuine pride in craftsmanship.
              </p>
              <div className="culture-pillars">
                {[
                  { icon: '💡', title: 'Innovation', desc: 'Constantly exploring new processes and materials to improve quality and speed.' },
                  { icon: '🤝', title: 'Collaboration', desc: 'Our engineers and clients work as one team, sharing goals and outcomes.' },
                  { icon: '📈', title: 'Growth', desc: 'We invest heavily in training and upskilling our workforce at every level.' },
                ].map(p => (
                  <div key={p.title} className="pillar">
                    <span className="pillar__icon">{p.icon}</span>
                    <div>
                      <strong>{p.title}</strong>
                      <span>{p.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="culture-imgs reveal slide-right">
              {[
                { label: 'SMT Assembly', img: 'https://images.pexels.com/photos/9242895/pexels-photo-9242895.jpeg', accent: '#2563eb' },
                { label: 'PCB Design', img: 'https://images.pexels.com/photos/4779887/pexels-photo-4779887.jpeg?auto=compress&cs=tinysrgb&w=400', accent: '#0891b2' },
                { label: 'Quality Lab', img: 'https://images.pexels.com/photos/18471527/pexels-photo-18471527.jpeg', accent: '#7c3aed' },
                { label: 'R&D Center', img: 'https://images.pexels.com/photos/2582937/pexels-photo-2582937.jpeg?auto=compress&cs=tinysrgb&w=400', accent: '#059669' },
                { label: 'Clean Room', img: 'https://images.pexels.com/photos/9485592/pexels-photo-9485592.jpeg', accent: '#dc2626' },
                { label: 'Logistics', img: 'https://images.pexels.com/photos/6432108/pexels-photo-6432108.jpeg', accent: '#d97706' },
              ].map(({ label, img, accent }, i) => (
                <div key={label} className="cult-img" style={{ '--delay': `${i * 0.08}s`, '--accent': accent }}>
                  <img src={img} alt={label} loading="lazy" />
                  <div className="cult-img__overlay" />
                  <span className="cult-img__label">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── VALUES ── */}
      <section className="section values-section">
        <div className="container">
          <div className="section-center reveal">
            <div className="section-tag tag--rose">Our Values</div>
            <h2 className="section-title">Principles That <span>Guide Everything</span></h2>
            <p className="section-subtitle">These aren't posters on a wall. They're the criteria we use when making every decision.</p>
          </div>
          <div className="values-grid">
            {values.map((v, i) => (
              <div key={v.title} className={`value-card ${v.color} reveal`} style={{ '--delay': `${i * 0.12}s` }}>
                <div className="value-card__bg-shape" />
                <div className="value-icon">{v.icon}</div>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CAPABILITIES ── */}
      <section className="section capabilities-section">
        <div className="container">
          <div className="section-center reveal">
            <div className="section-tag tag--cyan">Technical Capabilities</div>
            <h2 className="section-title">What We're <span>Built For</span></h2>
          </div>
          <div className="cap-grid">
            {capabilities.map((c, i) => (
              <div key={c.title} className={`cap-card ${c.bg} reveal`} style={{ '--delay': `${i * 0.1}s` }}>
                <div className="cap-icon">{c.icon}</div>
                <div className="cap-num">0{i + 1}</div>
                <h3>{c.title}</h3>
                <p>{c.desc}</p>
                <div className="cap-shine" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TIMELINE ── */}
    <section
  className="section timeline-section"
  style={{
    backgroundImage: "url('https://images.pexels.com/photos/7948063/pexels-photo-7948063.jpeg')",
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    position: 'relative'
  }}
>
  <div
    style={{
      position: 'absolute',
      inset: 0,
      background: 'rgba(0,0,0,0.6)'
    }}
  />
  
  <div className="container" style={{ position: 'relative', zIndex: 1 }}>
    <div className="section-center reveal">
      <div className="section-tag tag--indigo">Our Journey</div>
      <h2 className="section-title">
        Milestones That <span>Define Us</span>
      </h2>
    </div>

    <div className="timeline">
      <div className="timeline__line" />

      {milestones.map((m, i) => (
        <div
          key={m.year}
          className={`timeline-item ${i % 2 === 0 ? 'tl--left' : 'tl--right'} reveal`}
          style={{ '--delay': `${i * 0.15}s` }}
        >
          <div className="tl-card">
            <div className="tl-icon">{m.icon}</div>
            <div className="tl-year">{m.year}</div>
            <p>{m.event}</p>
          </div>

          <div className="tl-dot" />
        </div>
      ))}
    </div>
  </div>
</section>
      {/* ── CTA ── */}
      <section className="cta-section">
        <div className="cta-bg-mesh" />
        <div className="container cta-inner reveal">
          <div className="cta-text">
            <h2>Let's Build Something <span>Extraordinary</span></h2>
            <p>Whether you're a startup with a bold idea or an enterprise scaling production — we're your partner.</p>
          </div>
          <div className="cta-actions">
            <Link to="/contact" className="btn-glow">Start a Project →</Link>
            <Link to="/services" className="btn-outline-glow">Our Services</Link>
          </div>
        </div>
      </section>

    </main>
  );
}